import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Check, ArrowRight, GraduationCap, PenLine, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BOOKS = [
  {
    id: "mastering-pivot-trading",
    cover: "/books/mastering-pivot-trading.jpg",
    buy_url: "",
    price: 999,
    title: "Mastering Pivot Trading",
    subtitle: "The Complete Institutional Trading Framework",
    about:
      "A ground-up, structured guide to trading with pivots the way institutions frame the market. It moves from the mechanics of price equilibrium to a complete, rule-based decision system you can apply to any index or stock.",
    highlights: [
      "Central Pivot Range (CPR) — construction, meaning and width interpretation",
      "Traditional Pivot Points & Camarilla Pivot Points explained side by side",
      "7 CPR structural types and what each signals about the session ahead",
      "63 professional trading scenarios mapped to clear, repeatable actions",
      "Support/Resistance confluence, trend days vs range days, and trade planning",
    ],
  },
  {
    id: "systematic-fno-framework",
    cover: "/books/systematic-fno-framework.jpg",
    buy_url: "",
    price: 999,
    title: "Systematic F&O Trading Framework",
    subtitle: "A Complete Guide to Index Futures & Options Trading",
    about:
      "A disciplined, process-driven framework for trading index futures and options — built around the MAEE method (Market view, Analysis, Entry, Exit) with an unwavering focus on risk and psychology.",
    highlights: [
      "The MAEE Framework — a repeatable process from market view to exit",
      "15 structured strategies across directional, neutral and hedged setups",
      "Position sizing, stop placement and portfolio-level risk management",
      "Trading psychology, journaling and building consistency",
      "Practical playbooks for real market conditions",
    ],
  },
];

export default function Books() {
  const [books, setBooks] = useState(BOOKS);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/books").then((r) => { if (Array.isArray(r.data) && r.data.length) setBooks(r.data); }).catch(() => {});
  }, []);

  const loadRazorpay = () => new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });
  const buyOnSite = async (book) => {
    if (!user) { toast.message("Please log in to purchase — it keeps your order in your account."); navigate("/login"); return; }
    try {
      const { data } = await api.post("/checkout/order", { book_id: book.id });
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Could not load the payment window.");
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "KRV Prime", description: book.title,
        prefill: { name: data.user_name, email: data.user_email },
        theme: { color: "#D4AF37" },
        handler: async (r) => {
          try { await api.post("/checkout/verify", { ...r, book_id: book.id }); toast.success("Payment successful! Your book is in your Order History."); }
          catch { toast.error("Payment could not be verified."); }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Online purchase is not available yet.");
    }
  };
  return (
    <div data-testid="books-page">
      {/* Hero */}
      <section className="bg-navy grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-8 bg-gold" /><span className="eyebrow text-gold">Published Works</span></div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white leading-tight">Books by KRV Prime</h1>
          <p className="text-slate-300 mt-4 max-w-2xl">Two comprehensive frameworks distilled into systematic, in-depth reading — built for disciplined, process-driven market learning. Educational content only, not investment advice.</p>
        </div>
      </section>

      {/* Books */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">
        {books.map((b, i) => (
          <div key={b.title} className={`grid lg:grid-cols-5 gap-10 items-start ${i % 2 === 1 ? "" : ""}`} data-testid={`book-detail-${i}`}>
            <div className="lg:col-span-2">
              <div className="bg-navy grain rounded-xl p-8 flex items-center justify-center">
                <img src={b.cover} alt={b.title} className="w-56 h-auto rounded-md shadow-2xl ring-1 ring-white/10" data-testid={`book-page-cover-${i}`} />
              </div>
            </div>
            <div className="lg:col-span-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gold"><BookOpen className="h-3.5 w-3.5" /> Book {i + 1}</span>
              <h2 className="font-display text-3xl font-bold text-navy mt-2 leading-tight">{b.title}</h2>
              <p className="text-gold font-medium mt-1">{b.subtitle}</p>
              <p className="text-slate-600 leading-relaxed mt-5">{b.about}</p>
              <h3 className="font-display text-lg font-semibold text-navy mt-7 mb-3">What's inside</h3>
              <ul className="space-y-2.5">
                {b.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-slate-600">
                    <Check className="h-5 w-5 text-gold shrink-0 mt-0.5" /><span>{h}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                {b.price ? (
                  <button onClick={() => buyOnSite(b)} data-testid={`book-checkout-${i}`} className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-gold text-navy text-sm font-semibold hover:bg-gold-soft transition-colors">
                    Buy on KRV Prime · ₹{b.price} <ShoppingCart className="h-4 w-4" />
                  </button>
                ) : null}
                {b.buy_url && /^https?:/i.test(b.buy_url) ? (
                  <a href={b.buy_url} target="_blank" rel="noreferrer noopener" data-testid={`book-buy-${i}`} className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors">
                    Buy on Amazon Kindle <ArrowRight className="h-4 w-4" />
                  </a>
                ) : (
                  <Link to="/contact" data-testid={`book-buy-${i}`} className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors">
                    Buy on Amazon Kindle <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
                <Link to="/contact" data-testid={`book-enquire-${i}`} className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-border text-navy text-sm font-semibold hover:border-gold/50 transition-colors">
                  Enquire <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        ))}

        {/* About the author */}
        <div className="bg-subtle border border-border rounded-2xl p-8 sm:p-12" data-testid="about-author">
          <div className="flex items-center gap-3 mb-4"><PenLine className="h-5 w-5 text-gold" /><span className="eyebrow text-slate-500">About the Author</span></div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy leading-tight">Written by the KRV Prime</h2>
          <p className="text-slate-600 leading-relaxed mt-4 max-w-3xl">
            KRV Prime is a market-education brand focused on structured, research-driven learning. Both books are written
            by practitioners who study index and derivatives markets full time, combining institutional frameworks
            (CPR, pivots, participant open interest) with a rigorous, rule-based approach to analysis, risk and trading
            psychology. The goal is simple: help serious learners build repeatable, disciplined processes rather than chase tips.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[["Structured", "Frameworks over tips — every idea maps to a clear rule."], ["Research-led", "Grounded in market data, participant OI and price structure."], ["Disciplined", "Risk management and psychology at the core."]].map(([t, d]) => (
              <div key={t} className="bg-white border border-border rounded-lg p-5">
                <GraduationCap className="h-5 w-5 text-gold" />
                <p className="font-display font-semibold text-navy mt-3">{t}</p>
                <p className="text-sm text-slate-500 mt-1">{d}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-8">Educational content only. Nothing in these books constitutes investment advice or a recommendation to buy or sell any security.</p>
        </div>
      </div>
    </div>
  );
}
